import { ReactNode } from "react";
import { Box, Typography, alpha, darken, useTheme } from "@mui/material";
import { ShieldOutlined } from "@mui/icons-material";

interface Props {
  /**
   * Opcional: encabezado sobre `label`, con su texto. Ej. Flux: "Simulación de tu operación" y la nota de que el
   * ejemplo es sobre el 100 % de la línea.
   */
  intro?: { eyebrow: string; text: string };
  label: string;
  /** Monto ya formateado (`$12,345.67`). */
  amount: string;
  /** Línea bajo el monto (`MXN / mes durante 24 meses`). */
  caption: string;
  /** Acciones a la derecha del monto: el CTA y, si aplica, un enlace secundario. */
  actions: ReactNode;
  /** Nota al pie con el escudo; puede llevar un enlace. */
  disclaimer?: ReactNode;
  /**
   * Opcional: pie con contenido propio, en la franja bajo la línea y sin el escudo; si también hay `disclaimer`, va
   * debajo de él. Ej. Mercader y Flux: el check del aviso o de los términos, que se acepta en el cotizador.
   */
  footer?: ReactNode;
  /** Opcional: contenido bajo el `caption`, como el desglose de la renta del cotizador de WuWei. */
  children?: ReactNode;
  /**
   * Opcional: color de marca de la tarjeta (`--ld-blue` del diseño) cuando la marca no lo tiene en `primary.main`.
   * Pinta el halo, el borde degradado, el escudo, la etiqueta (oscurecido) y los bordes tenues. Ej. WuWei: botón
   * azul marino y tarjeta dorada (`primary.accent`). Sin él, la tarjeta queda como siempre.
   */
  brandColor?: string;
  /** Opcional: tamaño del monto cuando la pantalla lo baja del 34px del spec. Ej. WuWei: 30px, por el desglose. */
  amountFontSize?: number;
}

/** Neutros del spec (`--ld-text-2`, `--ld-text-3`); no varían por tenant. */
const TEXT_2 = "#4A5567";
const TEXT_3 = "#7A8497";

/** `.quote-hero-eyebrow`: la etiqueta y, si hay, el encabezado de `intro`. */
const eyebrowSx = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" } as const;

/** Franja a todo el ancho bajo la línea: la del aviso con el escudo y la del `footer`. */
const stripSx = {
  gridColumn: "1 / -1",
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  mt: { xs: 0, md: 2 },
  borderTop: "1px solid",
} as const;

/**
 * Replica `.quote-hero` del bloque "LENDIA REDESIGN v2" de styles.css, con los overrides de Flux:
 * - fondo con un halo radial de `primary.main` en la esquina superior derecha sobre blanco frío;
 * - borde de 1px en el tono claro de marca más un borde degradado (`::before`) que se desvanece;
 * - monto de 34px en `primary.dark` y aviso legal separado por una línea abajo.
 * Los colores de marca se derivan de la paleta del tenant.
 */
export const CardResumenCotizacion = ({
  intro,
  label,
  amount,
  caption,
  actions,
  disclaimer,
  footer,
  children,
  brandColor,
  amountFontSize = 34,
}: Props) => {
  const theme = useTheme();
  const brand = brandColor ?? theme.palette.primary.main;
  // Bordes tenues (`--ld-blue-100`) y etiqueta (`--ld-blue-700`): sin `brandColor`, los de siempre.
  const softBorder = brandColor ? alpha(brandColor, 0.3) : alpha(theme.palette.primary.accent, 0.35);
  const labelColor = brandColor
    ? darken(brandColor, 0.15)
    : (theme.palette.primary.hover ?? darken(theme.palette.primary.main, 0.35));

  return (
    <Box
      sx={{
        position: "relative",
        mt: "14px",
        p: { xs: "22px 20px 20px", md: "26px 30px 24px" },
        background: `radial-gradient(120% 100% at 100% 0%, ${alpha(brand, 0.05)} 0%, transparent 55%), linear-gradient(180deg, #F9FBFD 0%, #FDFEFE 100%)`,
        border: "1px solid",
        borderColor: softBorder,
        borderRadius: "14px",
        boxShadow: "0 4px 12px rgba(14,31,58,.08), 0 2px 4px rgba(14,31,58,.04)",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
        alignItems: "end",
        columnGap: 3,
        rowGap: { xs: 2, md: 0 },
        overflow: "hidden",
        // Borde degradado del diseño: más intenso arriba a la izquierda y se pierde hacia la derecha.
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          padding: "1.25px",
          background: `linear-gradient(118deg, ${alpha(brand, 0.55)} 0%, ${alpha(brand, 0.28)} 22%, ${alpha(brand, 0.08)} 48%, transparent 72%)`,
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
      }}
    >
      <Box>
        {intro && (
          <>
            <Typography sx={{ ...eyebrowSx, color: labelColor, mb: 1 }}>{intro.eyebrow}</Typography>
            <Typography sx={{ fontSize: 13.5, color: TEXT_2, mt: "-2px", mb: 2 }}>{intro.text}</Typography>
          </>
        )}
        <Typography
          sx={{
            ...eyebrowSx,
            // `--ld-blue-700` del diseño: el hover de la marca si lo define; si no, `main` oscurecido.
            color: labelColor,
            mb: intro ? 0.5 : 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            // 34px del spec: el número grande del cotizador, fuera de la escala del theme.
            fontSize: amountFontSize,
            fontWeight: 600,
            letterSpacing: "-0.025em",
            fontVariantNumeric: "tabular-nums",
            color: "primary.dark",
            lineHeight: 1.1,
            mb: 0.5,
          }}
        >
          {amount}
        </Typography>
        <Typography sx={{ fontSize: 13, color: TEXT_2 }}>{caption}</Typography>
        {children}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "stretch", md: "flex-end" },
          gap: 1.25,
        }}
      >
        {actions}
      </Box>

      {disclaimer && (
        <Box sx={{ ...stripSx, borderColor: softBorder, pt: "14px" }}>
          <ShieldOutlined sx={{ fontSize: 13, color: brand, mt: "2px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: TEXT_3, lineHeight: 1.55 }}>{disclaimer}</Typography>
        </Box>
      )}
      {footer && <Box sx={{ ...stripSx, borderColor: softBorder, pt: 2, ...(disclaimer && { mt: 2 }) }}>{footer}</Box>}
    </Box>
  );
};
